// Screen 7 · Grid entry — many similar items at once ("Add many").
// Answers: how do I enter 18 doors in one minute?
// The catalog item's own inputs are the columns. Standards and parents fill what they
// cover as locked values. A value that breaks a catalog rule warns on the row, never blocks.

import { useState } from 'react';
import type { LibCatalogInput, ResolvedItem, Scalar } from '@zerogap/engine';
import { insertRows, updateRow } from '../../lib/api';
import { nextNumber, prefixFor } from '../../lib/create';
import { readLength } from '../../lib/format';
import { ensureStored } from '../../lib/job';
import { Btn, Empty, Field, Info, Modal, Money } from '../../ui/kit';
import { MaterialPick, ValueEditor, showValue } from './ItemTab';
import type { JobCtx } from './Workspace';

interface Draft {
  number: string;
  values: Record<string, Scalar>;
}

export function GridEntry({ ctx, onClose }: { ctx: JobCtx; onClose: () => void }) {
  const lib = ctx.r.lib!;
  const res = ctx.r.res!;
  const cats = Object.values(lib.catalog).filter((c) => c.grid_enabled && !c.retired).sort((a, b) => a.name.localeCompare(b.name));
  const [catId, setCatId] = useState(cats[0]?.id ?? '');
  const [roomId, setRoomId] = useState('');
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [paste, setPaste] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const cat = lib.catalog[catId];
  const cols: LibCatalogInput[] = cat ? [...cat.inputs].sort((a, b) => a.sort - b.sort) : [];
  const existing = res.items.filter((i) => i.catalog_item_id === catId && !i.is_custom);
  const prefix = cat ? prefixFor(cat.name) : 'I';

  const numberFor = (taken: string[]) => nextNumber([...ctx.data.items.map((i) => i.number), ...taken], prefix);
  const addRow = () => setDrafts((d) => [...d, { number: numberFor(d.map((x) => x.number)), values: {} }]);

  const readCell = (col: LibCatalogInput, text: string): Scalar => {
    const t = text.trim();
    if (t === '') return null;
    if (col.data_type === 'number') return readLength(t) ?? null;
    if (col.data_type === 'boolean') return /^(true|yes|y|1|x)$/i.test(t);
    return t;
  };

  /** A pasted list: one row per line, cells split by tab or comma, in column order. */
  const takePaste = (text: string) => {
    const added: Draft[] = [];
    const taken = drafts.map((d) => d.number);
    for (const line of text.split(/\r?\n/)) {
      if (!line.trim()) continue;
      const cells = line.split(line.includes('\t') ? '\t' : ',');
      const values: Record<string, Scalar> = {};
      cols.forEach((c, i) => {
        const v = readCell(c, cells[i] ?? '');
        if (v !== null) values[c.key] = v;
      });
      const number = numberFor(taken);
      taken.push(number);
      added.push({ number, values });
    }
    setDrafts((d) => [...d, ...added]);
    setPaste(null);
  };

  const save = async () => {
    if (drafts.length === 0 || busy) return;
    setBusy(true);
    try {
      const start = Math.max(0, ...ctx.data.items.map((i) => i.sort ?? 0));
      await insertRows(
        'job_item',
        drafts.map((d, i) => ({ job_id: ctx.jobId, number: d.number, name: cat!.name, catalog_item_id: catId, inputs: d.values, sort: start + i + 1, ...(roomId ? { room_id: roomId } : {}) })),
      );
      setDrafts([]);
    } finally {
      setBusy(false);
    }
  };

  const writeExisting = async (item: ResolvedItem, key: string, value: Scalar, source: string) => {
    const id = await ensureStored(item, ctx.jobId, res.items);
    const row = ctx.data.items.find((i) => i.id === id);
    const field = source === 'parent' || source === 'standard' || source === 'override' ? 'overrides' : 'inputs';
    const next = { ...((row?.[field] as Record<string, Scalar>) ?? {}) };
    if (value === null || value === '') delete next[key];
    else next[key] = value;
    await updateRow('job_item', id, { [field]: next });
  };

  return (
    <Modal title="Add many" onClose={onClose} wide>
      {cats.length === 0 ? (
        <Empty>No catalog item has grid columns yet. Turn "grid entry" on for one in the Library.</Empty>
      ) : (
        <>
          <div className="row mid" style={{ marginBottom: 10 }}>
            <Field label="Catalog item · its inputs are the columns">
              <select
                value={catId}
                onChange={(e) => {
                  setCatId(e.target.value);
                  setDrafts([]);
                }}
              >
                {cats.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </Field>
            {ctx.data.rooms.length > 0 && (
            <Field label="Room · new rows land here">
              <select value={roomId} onChange={(e) => setRoomId(e.target.value)}>
                <option value="">No room · the job's standards</option>
                {ctx.data.rooms.map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.name}
                  </option>
                ))}
              </select>
            </Field>
            )}
            <span className="grow" />
            <Btn small onClick={() => setPaste('')}>
              Paste list
            </Btn>
            <Btn small onClick={addRow}>
              + Row
            </Btn>
          </div>

          {paste !== null && (
            <div className="panel" style={{ marginBottom: 10 }}>
              <div className="lb">
                <span>Paste rows · columns in this order: {cols.map((c) => c.label).join(', ')}</span>
              </div>
              <textarea autoFocus value={paste} placeholder={'One row per line, cells separated by tabs (as copied from a spreadsheet) or commas.\nFractions are fine: 24 3/8'} onChange={(e) => setPaste(e.target.value)} />
              <div className="row mid" style={{ marginTop: 6, gap: 6 }}>
                <Btn small primary disabled={!paste.trim()} onClick={() => takePaste(paste)}>
                  Add these rows
                </Btn>
                <Btn small onClick={() => setPaste(null)}>
                  Cancel
                </Btn>
              </div>
            </div>
          )}

          <div className="tblwrap" style={{ maxHeight: '55vh', overflow: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  {cols.map((c) => (
                    <th key={c.key}>
                      {c.label}
                      {c.info && <Info>{c.info}</Info>}
                    </th>
                  ))}
                  <th className="right">Price</th>
                </tr>
              </thead>
              <tbody>
                {existing.map((item) => {
                  const parent = item.parent_item_id ? res.items.find((i) => i.id === item.parent_item_id) : null;
                  return (
                    <tr key={item.id}>
                      <td className="nowrap">
                        <b>{item.number}</b>
                        {parent && (
                          <div>
                            <small>from {parent.number}</small>
                          </div>
                        )}
                        {item.rule_warnings.map((w) => (
                          <div key={w} className="warn">
                            {w}
                          </div>
                        ))}
                      </td>
                      {item.inputs.map((inp) => (
                        <td key={inp.key}>
                          {inp.hidden ? (
                            <span className="muted" title="Hidden on this item by its rule">—</span>
                          ) : inp.locked || (inp.standard_key && inp.can_override === false) ? (
                            <span className="lk" title={inp.source === 'parent' ? 'Sized by its parent. Edit the parent.' : 'Filled by a job standard.'}>
                              {inp.data_type === 'material' ? lib.materials[String(inp.value ?? '')]?.description ?? '—' : showValue(inp.value, inp.data_type, inp.unit)}
                            </span>
                          ) : inp.data_type === 'material' ? (
                            <MaterialPick lib={lib} value={String(inp.value ?? '')} filter={inp.material_filter} onPick={(id) => void writeExisting(item, inp.key, id, inp.source)} />
                          ) : (
                            <span className={inp.source === 'override' ? 'ov' : ''}>
                              <ValueEditor type={inp.data_type} unit={inp.unit} choices={inp.choices} value={inp.value} disabled={ctx.locked} onSave={(v) => void writeExisting(item, inp.key, v, inp.source)} />
                            </span>
                          )}
                        </td>
                      ))}
                      <td className="right">
                        <Money cents={item.price_cents} />
                      </td>
                    </tr>
                  );
                })}
                {drafts.map((d, n) => (
                  <tr key={`new-${n}`} className="sel">
                    <td>
                      <input type="text" className="cell" style={{ width: 80 }} value={d.number} onChange={(e) => setDrafts(drafts.map((x, k) => (k === n ? { ...x, number: e.target.value } : x)))} />
                    </td>
                    {cols.map((c) => (
                      <td key={c.key}>
                        {c.data_type === 'standard' ? (
                          <span className="lk" title="Follows a standard: filled once the row is added">from the standard</span>
                        ) : c.data_type === 'material' ? (
                          <MaterialPick lib={lib} value={String(d.values[c.key] ?? '')} filter={c.material_filter} onPick={(id) => setDrafts(drafts.map((x, k) => (k === n ? { ...x, values: { ...x.values, [c.key]: id } } : x)))} />
                        ) : (
                          <ValueEditor type={c.data_type} unit={c.unit} choices={c.choices} value={d.values[c.key] ?? null} onSave={(v) => setDrafts(drafts.map((x, k) => (k === n ? { ...x, values: { ...x.values, [c.key]: v } } : x)))} />
                        )}
                      </td>
                    ))}
                    <td className="right">
                      <Btn small onClick={() => setDrafts(drafts.filter((_, k) => k !== n))}>
                        ×
                      </Btn>
                    </td>
                  </tr>
                ))}
                {existing.length === 0 && drafts.length === 0 && (
                  <tr>
                    <td colSpan={cols.length + 2}>
                      <Empty>None on this job yet. Add a row, or paste a list.</Empty>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="row mid" style={{ marginTop: 10, justifyContent: 'space-between' }}>
            <small>Rows already on the job save as you type. New rows (highlighted) are added together.</small>
            <Btn primary disabled={drafts.length === 0 || busy} onClick={() => void save()}>
              Add {drafts.length || ''} {drafts.length === 1 ? 'item' : 'items'}
            </Btn>
          </div>
        </>
      )}
    </Modal>
  );
}
